export const LANDMARK_MHD_TYPES = ["METRO", "TRAIN", "TRAM", "BUS"];

export const LANDMARK_MHD_TYPE_LABELS = {
  METRO: "=M=",
  TRAIN: "Vlak",
  TRAM: "Tramvaj",
  BUS: "Autobus",
};

export const LANDMARK_MHD_TYPE_MARKERS = {
  METRO: "=M=",
  TRAIN: "V",
  TRAM: "T",
  BUS: "B",
};

export const LANDMARK_MHD_COLORS = {
  METRO: "#0a2d7c",
  TRAIN: "#009640",
  TRAM: "#e21039",
  BUS: "#f97316",
};

export const LANDMARK_CATEGORIES = [
  { key: "mhd", label: "MHD", status: "ready" },
  { key: "parks", label: "Parky", status: "placeholder" },
  { key: "playgrounds", label: "Hřiště", status: "placeholder" },
  { key: "billboards", label: "Billboardy", status: "placeholder" },
];

export const ADDRESS_ENRICHMENT_PRECINCT_IDENTITY = "area_csu_code:okrsek_code";

function cleanAddressToken(value) {
  if (value === null || value === undefined || typeof value === "boolean") return "";
  if (typeof value === "number" && !Number.isFinite(value)) return "";
  return String(value).replace(/\u00a0/g, " ").trim();
}

function firstCleanAddressToken(...values) {
  for (const value of values) {
    const cleaned = cleanAddressToken(value);
    if (cleaned) return cleaned;
  }
  return "";
}

function parseCompositePrecinctId(value) {
  const cleaned = cleanAddressToken(value);
  if (!cleaned) return null;
  const separatorIndex = cleaned.indexOf(":");
  if (separatorIndex <= 0 || separatorIndex === cleaned.length - 1) return null;
  const areaCode = cleanAddressToken(cleaned.slice(0, separatorIndex));
  const precinctCode = cleanAddressToken(cleaned.slice(separatorIndex + 1));
  return areaCode && precinctCode ? { areaCode, precinctCode } : null;
}

export function buildAddressEnrichmentPrecinctId(feature) {
  const properties = feature?.properties || {};
  const existingComposite = parseCompositePrecinctId(properties.precinct_id || properties.precinctId);
  const areaCode = firstCleanAddressToken(
    properties.area_csu_code,
    properties.areaCsuCode,
    properties.selector_kod_mco,
    properties.kod_mco,
    existingComposite?.areaCode,
    properties.selector_kod_obec,
    properties.kod_obec
  );
  const precinctCode = firstCleanAddressToken(
    properties.okrsek_code,
    properties.okrsekCode,
    properties.okrsek,
    existingComposite?.precinctCode,
    properties.district_code,
    properties.districtCode,
    properties.cislo
  );

  if (!areaCode || !precinctCode) return null;
  return `${areaCode}:${precinctCode}`;
}

function normalizeAddressStreetSummary(street) {
  if (!street || typeof street !== "object" || Array.isArray(street)) return null;
  const streetName = firstCleanAddressToken(street.street, street.street_name, street.name);
  const addressCount = Math.max(0, Number(street.address_count ?? street.addressCount ?? 0) || 0);
  const houseNumbersSample = Array.isArray(street.house_numbers_sample)
    ? street.house_numbers_sample.map(cleanAddressToken).filter(Boolean)
    : Array.isArray(street.houseNumbersSample)
      ? street.houseNumbersSample.map(cleanAddressToken).filter(Boolean)
      : [];
  if (!streetName && !addressCount && !houseNumbersSample.length) return null;
  return {
    street: streetName,
    addressCount,
    houseNumbersSample,
  };
}

function unavailableAddressEnrichmentIndex(reason = "not_available") {
  return {
    available: false,
    reason,
    precinctIdentity: ADDRESS_ENRICHMENT_PRECINCT_IDENTITY,
    precincts: new Map(),
  };
}

export function normalizeAddressEnrichmentIndex(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return unavailableAddressEnrichmentIndex("invalid_payload");
  }
  if (payload.available === false) {
    return unavailableAddressEnrichmentIndex(cleanAddressToken(payload.reason) || "not_available");
  }

  const rawIndex = payload.available === true && payload.data && typeof payload.data === "object" ? payload.data : payload;
  if (!rawIndex || typeof rawIndex !== "object" || Array.isArray(rawIndex)) {
    return unavailableAddressEnrichmentIndex("invalid_payload");
  }

  const wrapperElectionKey = cleanAddressToken(payload.election_key ?? payload.electionKey);
  const wrapperAreaSlug = cleanAddressToken(payload.area_slug ?? payload.areaSlug);
  const electionKey = cleanAddressToken(rawIndex.election_key ?? rawIndex.electionKey);
  const areaSlug = cleanAddressToken(rawIndex.area_slug ?? rawIndex.areaSlug);
  if ((wrapperElectionKey && electionKey && wrapperElectionKey !== electionKey) || (wrapperAreaSlug && areaSlug && wrapperAreaSlug !== areaSlug)) {
    return unavailableAddressEnrichmentIndex("identity_mismatch");
  }

  const precinctIdentity = cleanAddressToken(rawIndex.precinct_identity ?? rawIndex.precinctIdentity);
  const rawPrecincts = rawIndex.precincts;
  if (precinctIdentity !== ADDRESS_ENRICHMENT_PRECINCT_IDENTITY || !rawPrecincts || typeof rawPrecincts !== "object" || Array.isArray(rawPrecincts)) {
    return unavailableAddressEnrichmentIndex("invalid_payload");
  }

  const precincts = new Map();
  for (const [rawPrecinctId, rawSummary] of Object.entries(rawPrecincts)) {
    const precinctId = cleanAddressToken(rawPrecinctId);
    const parsedPrecinctId = parseCompositePrecinctId(precinctId);
    if (!parsedPrecinctId || !rawSummary || typeof rawSummary !== "object" || Array.isArray(rawSummary)) continue;
    const okrsekCode = firstCleanAddressToken(rawSummary.okrsek_code, rawSummary.okrsekCode, parsedPrecinctId.precinctCode);
    const addressCount = Math.max(0, Number(rawSummary.address_count ?? rawSummary.addressCount ?? 0) || 0);
    const streetCount = Math.max(0, Number(rawSummary.street_count ?? rawSummary.streetCount ?? 0) || 0);
    const streets = Array.isArray(rawSummary.streets) ? rawSummary.streets.map(normalizeAddressStreetSummary).filter(Boolean) : [];
    precincts.set(precinctId, {
      precinctId,
      okrsekCode,
      addressCount,
      streetCount,
      streets,
    });
  }

  if (!precincts.size) return unavailableAddressEnrichmentIndex("invalid_payload");
  return {
    available: true,
    precinctIdentity: ADDRESS_ENRICHMENT_PRECINCT_IDENTITY,
    electionKey: electionKey || wrapperElectionKey,
    areaSlug: areaSlug || wrapperAreaSlug,
    precincts,
  };
}

export function getAddressSummaryForFeature(index, feature) {
  if (!index?.available || !(index.precincts instanceof Map)) return null;
  const precinctId = buildAddressEnrichmentPrecinctId(feature);
  if (!precinctId) return null;
  return index.precincts.get(precinctId) || null;
}

function getAddressPrecinctIdFromIndex(feature, addressIndex) {
  const directPrecinctId = buildAddressEnrichmentPrecinctId(feature);
  if (directPrecinctId) return directPrecinctId;
  if (!addressIndex?.available || !(addressIndex.precincts instanceof Map)) return null;

  const properties = feature?.properties || {};
  const precinctCode = firstCleanAddressToken(
    properties.okrsek_code,
    properties.okrsekCode,
    properties.okrsek,
    properties.district_code,
    properties.districtCode,
    properties.cislo
  );
  if (!precinctCode) return null;

  const matches = [];
  for (const [precinctId, summary] of addressIndex.precincts.entries()) {
    if (cleanAddressToken(summary?.okrsekCode) === precinctCode) matches.push(cleanAddressToken(precinctId));
  }
  return matches.length === 1 ? matches[0] : null;
}

function unavailableAddressEnrichmentPoints(reason = "not_available") {
  return {
    available: false,
    reason,
    precinctIdentity: ADDRESS_ENRICHMENT_PRECINCT_IDENTITY,
    precincts: new Map(),
  };
}

export function normalizeAddressEnrichmentPoints(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return unavailableAddressEnrichmentPoints("invalid_payload");
  }
  if (payload.available === false) {
    return unavailableAddressEnrichmentPoints(cleanAddressToken(payload.reason) || "not_available");
  }

  const rawPoints = payload.available === true && payload.data && typeof payload.data === "object" ? payload.data : payload;
  if (!rawPoints || typeof rawPoints !== "object" || Array.isArray(rawPoints)) {
    return unavailableAddressEnrichmentPoints("invalid_payload");
  }

  const wrapperElectionKey = cleanAddressToken(payload.election_key ?? payload.electionKey);
  const wrapperAreaSlug = cleanAddressToken(payload.area_slug ?? payload.areaSlug);
  const electionKey = cleanAddressToken(rawPoints.election_key ?? rawPoints.electionKey);
  const areaSlug = cleanAddressToken(rawPoints.area_slug ?? rawPoints.areaSlug);
  if ((wrapperElectionKey && electionKey && wrapperElectionKey !== electionKey) || (wrapperAreaSlug && areaSlug && wrapperAreaSlug !== areaSlug)) {
    return unavailableAddressEnrichmentPoints("identity_mismatch");
  }

  const precinctIdentity = cleanAddressToken(rawPoints.precinct_identity ?? rawPoints.precinctIdentity);
  const rawPrecincts = rawPoints.precincts;
  if (precinctIdentity !== ADDRESS_ENRICHMENT_PRECINCT_IDENTITY || !rawPrecincts || typeof rawPrecincts !== "object" || Array.isArray(rawPrecincts)) {
    return unavailableAddressEnrichmentPoints("invalid_payload");
  }

  const precincts = new Map();
  for (const [rawPrecinctId, rawRecords] of Object.entries(rawPrecincts)) {
    const precinctId = cleanAddressToken(rawPrecinctId);
    const parsedPrecinctId = parseCompositePrecinctId(precinctId);
    if (!parsedPrecinctId || !Array.isArray(rawRecords)) continue;
    const records = rawRecords
      .map((record) => {
        if (!record || typeof record !== "object" || Array.isArray(record)) return null;
        const street = firstCleanAddressToken(record.street, record.street_name, record.name);
        const orientationNumber = firstCleanAddressToken(record.orientation_number, record.orientationNumber);
        const houseNumber = firstCleanAddressToken(record.house_number, record.houseNumber);
        if (!street || (!orientationNumber && !houseNumber)) return null;
        return {
          street,
          orientationNumber,
          houseNumber,
        };
      })
      .filter(Boolean);
    precincts.set(precinctId, records);
  }

  if (!precincts.size) return unavailableAddressEnrichmentPoints("invalid_payload");
  return {
    available: true,
    precinctIdentity: ADDRESS_ENRICHMENT_PRECINCT_IDENTITY,
    electionKey: electionKey || wrapperElectionKey,
    areaSlug: areaSlug || wrapperAreaSlug,
    precincts,
  };
}

export function getAddressPointsForFeature(pointsIndex, feature, addressIndex) {
  if (!pointsIndex?.available || !(pointsIndex.precincts instanceof Map)) return [];
  const precinctId = getAddressPrecinctIdFromIndex(feature, addressIndex);
  if (!precinctId) return [];
  return pointsIndex.precincts.get(precinctId) || [];
}

export function formatAddressPointForDisplay(record) {
  const street = cleanAddressToken(record?.street);
  const orientationNumber = cleanAddressToken(record?.orientationNumber ?? record?.orientation_number);
  const houseNumber = cleanAddressToken(record?.houseNumber ?? record?.house_number);
  const number = orientationNumber || houseNumber;
  return street && number ? `${street} ${number}` : "";
}

function parseNumericPrefix(value) {
  const text = cleanAddressToken(value);
  const match = text.match(/^(\d+)/);
  if (!match) return { number: null, text };
  return { number: Number(match[1]), text };
}

function compareAddressNumberTokens(a, b) {
  const parsedA = parseNumericPrefix(a);
  const parsedB = parseNumericPrefix(b);
  if (parsedA.number != null && parsedB.number != null && parsedA.number !== parsedB.number) {
    return parsedA.number - parsedB.number;
  }
  if (parsedA.number != null && parsedB.number == null) return -1;
  if (parsedA.number == null && parsedB.number != null) return 1;
  return parsedA.text.localeCompare(parsedB.text, "cs-CZ", { numeric: true, sensitivity: "base" });
}

function getAddressSortGroup(record) {
  const orientationNumber = cleanAddressToken(record?.orientationNumber ?? record?.orientation_number);
  if (orientationNumber) {
    const parsed = parseNumericPrefix(orientationNumber);
    if (parsed.number != null) return parsed.number % 2 === 1 ? 0 : 1;
    return 2;
  }
  return 3;
}

export function sortAddressPointsForDisplay(records) {
  const decorated = (Array.isArray(records) ? records : [])
    .map((record, index) => ({
      record,
      index,
      label: formatAddressPointForDisplay(record),
    }))
    .filter((item) => item.label);

  decorated.sort((a, b) => {
    const streetCompare = cleanAddressToken(a.record?.street).localeCompare(cleanAddressToken(b.record?.street), "cs-CZ", {
      numeric: true,
      sensitivity: "base",
    });
    if (streetCompare !== 0) return streetCompare;

    const groupCompare = getAddressSortGroup(a.record) - getAddressSortGroup(b.record);
    if (groupCompare !== 0) return groupCompare;

    const numberA = firstCleanAddressToken(a.record?.orientationNumber, a.record?.orientation_number, a.record?.houseNumber, a.record?.house_number);
    const numberB = firstCleanAddressToken(b.record?.orientationNumber, b.record?.orientation_number, b.record?.houseNumber, b.record?.house_number);
    const numberCompare = compareAddressNumberTokens(numberA, numberB);
    return numberCompare !== 0 ? numberCompare : a.index - b.index;
  });

  const seen = new Set();
  const out = [];
  for (const item of decorated) {
    const key = item.label.toLocaleLowerCase("cs-CZ");
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item.record);
  }
  return out;
}

export function groupAddressPointsForDisplay(records) {
  const grouped = [];
  const byStreet = new Map();

  for (const record of sortAddressPointsForDisplay(records)) {
    const street = cleanAddressToken(record?.street);
    const number = firstCleanAddressToken(record?.orientationNumber, record?.orientation_number, record?.houseNumber, record?.house_number);
    if (!street || !number) continue;

    const streetKey = street.toLocaleLowerCase("cs-CZ");
    let group = byStreet.get(streetKey);
    if (!group) {
      group = {
        street,
        numbers: [],
        seenNumbers: new Set(),
      };
      byStreet.set(streetKey, group);
      grouped.push(group);
    }

    const numberKey = number.toLocaleLowerCase("cs-CZ");
    if (group.seenNumbers.has(numberKey)) continue;
    group.seenNumbers.add(numberKey);
    group.numbers.push(number);
  }

  return grouped.map(({ street, numbers }) => ({ street, numbers }));
}

export function splitRouteTypes(value) {
  return String(value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function classifyMhdRouteTypes(routeTypeValue) {
  const types = new Set();
  for (const routeType of splitRouteTypes(routeTypeValue)) {
    if (routeType === "1") types.add("METRO");
    else if (routeType === "0") types.add("TRAM");
    else if (routeType === "2") types.add("TRAIN");
    else if (routeType === "3" || routeType === "4" || routeType === "11") types.add("BUS");
  }
  return Array.from(types);
}

export function normalizeStringList(value) {
  return Array.from(
    new Set(
      String(value ?? "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

export function normalizeMhdStopsPayload(payload) {
  const rawStops = Array.isArray(payload?.stops)
    ? payload.stops
    : Array.isArray(payload?.markers)
      ? payload.markers
      : Array.isArray(payload?.features)
        ? payload.features
        : [];

  return rawStops
    .map((item, index) => {
      const properties = item && item.type === "Feature" ? item.properties || {} : item || {};
      const coordinates = item && item.type === "Feature" ? item.geometry?.coordinates : null;
      const lng = coordinates ? Number(coordinates[0]) : Number(properties.lng);
      const lat = coordinates ? Number(coordinates[1]) : Number(properties.lat);
      const type = String(properties.type || "").trim().toUpperCase();
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || !LANDMARK_MHD_TYPES.includes(type)) return null;

      return {
        id: String(properties.id || `${type}-${index}`),
        type,
        typeLabel: LANDMARK_MHD_TYPE_LABELS[type] || type,
        stopName: String(properties.stopName || properties.stop_name || "Zastávka").trim(),
        lat,
        lng,
        platformCount: Math.max(1, Number(properties.platformCount || properties.platform_count || 1) || 1),
        platformCodes: Array.isArray(properties.platformCodes)
          ? properties.platformCodes.map(String).filter(Boolean)
          : normalizeStringList(properties.platformCodes || properties.platform_codes || properties.platform_code),
        routes: Array.isArray(properties.routes)
          ? properties.routes.map(String).filter(Boolean)
          : normalizeStringList(properties.routes || properties.routes_names),
        zones: Array.isArray(properties.zones)
          ? properties.zones.map(String).filter(Boolean)
          : normalizeStringList(properties.zones || properties.zones_id),
        hasNight: Boolean(properties.hasNight || properties.has_night),
        hasRegional: Boolean(properties.hasRegional || properties.has_regional),
        wheelchairBoarding: String(properties.wheelchairBoarding ?? properties.wheelchair_boarding ?? "").trim(),
        validity: String(properties.validity || "").trim(),
      };
    })
    .filter(Boolean);
}

export function getGeoJsonBounds(geoJson) {
  const features = Array.isArray(geoJson?.features) ? geoJson.features : [];
  let minLat = Infinity;
  let minLng = Infinity;
  let maxLat = -Infinity;
  let maxLng = -Infinity;

  function visit(value) {
    if (!Array.isArray(value)) return;
    if (value.length >= 2 && Number.isFinite(Number(value[0])) && Number.isFinite(Number(value[1]))) {
      const lng = Number(value[0]);
      const lat = Number(value[1]);
      minLat = Math.min(minLat, lat);
      minLng = Math.min(minLng, lng);
      maxLat = Math.max(maxLat, lat);
      maxLng = Math.max(maxLng, lng);
      return;
    }
    value.forEach(visit);
  }

  features.forEach((feature) => visit(feature?.geometry?.coordinates));
  if (![minLat, minLng, maxLat, maxLng].every(Number.isFinite)) return null;

  return { minLat, minLng, maxLat, maxLng };
}

function getPointBounds(ring) {
  let minLat = Infinity;
  let minLng = Infinity;
  let maxLat = -Infinity;
  let maxLng = -Infinity;
  for (const point of Array.isArray(ring) ? ring : []) {
    const lng = Number(point?.[0]);
    const lat = Number(point?.[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    minLat = Math.min(minLat, lat);
    minLng = Math.min(minLng, lng);
    maxLat = Math.max(maxLat, lat);
    maxLng = Math.max(maxLng, lng);
  }
  if (![minLat, minLng, maxLat, maxLng].every(Number.isFinite)) return null;
  return { minLat, minLng, maxLat, maxLng };
}

function mergeBounds(boundsList) {
  const valid = (Array.isArray(boundsList) ? boundsList : []).filter(Boolean);
  if (!valid.length) return null;
  return valid.reduce(
    (acc, bounds) => ({
      minLat: Math.min(acc.minLat, bounds.minLat),
      minLng: Math.min(acc.minLng, bounds.minLng),
      maxLat: Math.max(acc.maxLat, bounds.maxLat),
      maxLng: Math.max(acc.maxLng, bounds.maxLng),
    }),
    { minLat: Infinity, minLng: Infinity, maxLat: -Infinity, maxLng: -Infinity }
  );
}

function normalizePolygonRings(coordinates) {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return null;
  const rings = coordinates
    .map((ring) =>
      (Array.isArray(ring) ? ring : [])
        .map((point) => [Number(point?.[0]), Number(point?.[1])])
        .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat))
    )
    .filter((ring) => ring.length >= 3);
  if (!rings.length) return null;
  const bounds = mergeBounds(rings.map(getPointBounds));
  return bounds ? { rings, bounds } : null;
}

export function buildAreaGeometryIndex(geoJson) {
  const polygons = [];
  for (const feature of Array.isArray(geoJson?.features) ? geoJson.features : []) {
    const geometry = feature?.geometry;
    if (geometry?.type === "Polygon") {
      const polygon = normalizePolygonRings(geometry.coordinates);
      if (polygon) polygons.push(polygon);
    } else if (geometry?.type === "MultiPolygon") {
      for (const coordinates of Array.isArray(geometry.coordinates) ? geometry.coordinates : []) {
        const polygon = normalizePolygonRings(coordinates);
        if (polygon) polygons.push(polygon);
      }
    }
  }
  return {
    polygons,
    bounds: mergeBounds(polygons.map((polygon) => polygon.bounds)),
  };
}

function isInsideBounds(lat, lng, bounds, tolerance = 0) {
  if (!bounds) return false;
  const pad = Math.max(0, Number(tolerance) || 0);
  return (
    lat >= bounds.minLat - pad &&
    lat <= bounds.maxLat + pad &&
    lng >= bounds.minLng - pad &&
    lng <= bounds.maxLng + pad
  );
}

function isPointInRing(lat, lng, ring) {
  let inside = false;
  const points = Array.isArray(ring) ? ring : [];
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const xi = Number(points[i]?.[0]);
    const yi = Number(points[i]?.[1]);
    const xj = Number(points[j]?.[0]);
    const yj = Number(points[j]?.[1]);
    if (![xi, yi, xj, yj].every(Number.isFinite)) continue;
    const crossesLatitude = (yi > lat) !== (yj > lat);
    const intersectionLng = ((xj - xi) * (lat - yi)) / (yj - yi || 1e-12) + xi;
    const intersects = crossesLatitude && lng < intersectionLng;
    if (intersects) inside = !inside;
  }
  return inside;
}

export function isMarkerInsideAreaGeometry(marker, areaGeometryIndex, toleranceDegrees = 0) {
  const lat = Number(marker?.lat);
  const lng = Number(marker?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  const polygons = Array.isArray(areaGeometryIndex?.polygons) ? areaGeometryIndex.polygons : [];
  if (!polygons.length) return true;
  if (!isInsideBounds(lat, lng, areaGeometryIndex.bounds, toleranceDegrees)) return false;

  for (const polygon of polygons) {
    if (!isInsideBounds(lat, lng, polygon.bounds, toleranceDegrees)) continue;
    const [outerRing, ...holes] = polygon.rings;
    if (!isPointInRing(lat, lng, outerRing)) continue;
    if (holes.some((hole) => isPointInRing(lat, lng, hole))) continue;
    return true;
  }
  return false;
}

export function isMarkerInsideBounds(marker, bounds, paddingRatio = 0.08) {
  if (!bounds) return true;
  const lat = Number(marker?.lat);
  const lng = Number(marker?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  const latPad = Math.max((bounds.maxLat - bounds.minLat) * paddingRatio, 0.001);
  const lngPad = Math.max((bounds.maxLng - bounds.minLng) * paddingRatio, 0.001);
  return (
    lat >= bounds.minLat - latPad &&
    lat <= bounds.maxLat + latPad &&
    lng >= bounds.minLng - lngPad &&
    lng <= bounds.maxLng + lngPad
  );
}

function getRingAreaAndCentroid(ring) {
  const points = Array.isArray(ring) ? ring : [];
  let twiceArea = 0;
  let centroidLng = 0;
  let centroidLat = 0;

  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const x0 = Number(points[j]?.[0]);
    const y0 = Number(points[j]?.[1]);
    const x1 = Number(points[i]?.[0]);
    const y1 = Number(points[i]?.[1]);
    if (![x0, y0, x1, y1].every(Number.isFinite)) continue;
    const cross = x0 * y1 - x1 * y0;
    twiceArea += cross;
    centroidLng += (x0 + x1) * cross;
    centroidLat += (y0 + y1) * cross;
  }

  if (Math.abs(twiceArea) < 1e-12) return null;
  return {
    area: Math.abs(twiceArea) / 2,
    lng: centroidLng / (3 * twiceArea),
    lat: centroidLat / (3 * twiceArea),
  };
}

function getRingAveragePoint(ring) {
  let lngSum = 0;
  let latSum = 0;
  let count = 0;
  for (const point of Array.isArray(ring) ? ring : []) {
    const lng = Number(point?.[0]);
    const lat = Number(point?.[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
    lngSum += lng;
    latSum += lat;
    count += 1;
  }
  return count > 0 ? { lat: latSum / count, lng: lngSum / count } : null;
}

function isPointInPolygon(lat, lng, polygon) {
  if (!isInsideBounds(lat, lng, polygon?.bounds)) return false;
  const [outerRing, ...holes] = Array.isArray(polygon?.rings) ? polygon.rings : [];
  if (!outerRing || !isPointInRing(lat, lng, outerRing)) return false;
  return !holes.some((hole) => isPointInRing(lat, lng, hole));
}

function getPolygonRepresentativePoint(polygon) {
  const [outerRing] = Array.isArray(polygon?.rings) ? polygon.rings : [];
  if (!outerRing) return null;
  const candidates = [];
  const centroid = getRingAreaAndCentroid(outerRing);
  if (centroid) candidates.push({ lat: centroid.lat, lng: centroid.lng });
  if (polygon.bounds) {
    candidates.push({
      lat: (polygon.bounds.minLat + polygon.bounds.maxLat) / 2,
      lng: (polygon.bounds.minLng + polygon.bounds.maxLng) / 2,
    });
  }
  const average = getRingAveragePoint(outerRing);
  if (average) candidates.push(average);

  return candidates.find((point) => isPointInPolygon(point.lat, point.lng, polygon)) || average || null;
}

export function getFeatureRepresentativePoint(feature) {
  const geometry = feature?.geometry;
  const polygons = [];

  if (geometry?.type === "Polygon") {
    const polygon = normalizePolygonRings(geometry.coordinates);
    if (polygon) polygons.push(polygon);
  } else if (geometry?.type === "MultiPolygon") {
    for (const coordinates of Array.isArray(geometry.coordinates) ? geometry.coordinates : []) {
      const polygon = normalizePolygonRings(coordinates);
      if (polygon) polygons.push(polygon);
    }
  }

  const ranked = polygons
    .map((polygon) => ({
      polygon,
      area: Math.max(...polygon.rings.map((ring) => getRingAreaAndCentroid(ring)?.area || 0)),
    }))
    .sort((a, b) => b.area - a.area);

  for (const item of ranked) {
    const point = getPolygonRepresentativePoint(item.polygon);
    if (point) return point;
  }
  return null;
}

export function filterMhdStops(stops, { enabledTypes, geoJsonBounds, areaGeometryIndex, viewportBounds, maxMarkers = 900 } = {}) {
  const typeSet = new Set(Array.isArray(enabledTypes) ? enabledTypes : LANDMARK_MHD_TYPES);
  const filtered = (Array.isArray(stops) ? stops : [])
    .filter((stop) => typeSet.has(stop.type))
    .filter((stop) => isMarkerInsideBounds(stop, geoJsonBounds))
    .filter((stop) => isMarkerInsideAreaGeometry(stop, areaGeometryIndex))
    .filter((stop) => {
      if (!viewportBounds) return true;
      const lat = Number(stop.lat);
      const lng = Number(stop.lng);
      return (
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        lat >= viewportBounds.minLat &&
        lat <= viewportBounds.maxLat &&
        lng >= viewportBounds.minLng &&
        lng <= viewportBounds.maxLng
      );
    });

  return filtered.slice(0, Math.max(0, Number(maxMarkers) || 0));
}
